import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../utils/api';

const SESSION_KEY = 'gng_storeClosedReminderShown';

/**
 * Friendly once-per-session reminder shown to owners when their store is manually closed.
 * Reads /api/owners/me; renders nothing unless business.isManuallyClosed === true and the
 * reminder hasn't been dismissed yet this session. Owners can tap "Open my store" to flip
 * the flag back on without leaving the modal.
 */
export default function OwnerStoreClosedReminder() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const prefix = location.pathname.startsWith('/__test__') ? '/__test__' : '';
  const [show, setShow] = useState(false);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api('/api/owners/me', { getToken });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data?.business?.isManuallyClosed === true) {
          setShow(true);
        }
      } catch {
        // Non-fatal: don't block the page if /me fails
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn]);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setShow(false);
  };

  const handleOpenStore = async () => {
    if (opening) return;
    setOpening(true);
    try {
      const res = await api('/api/owners/me/business/store-status', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isManuallyClosed: false }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      sessionStorage.setItem(SESSION_KEY, '1');
      setShow(false);
    } catch (err) {
      alert(err.message || 'Could not open the store. Please try again.');
    } finally {
      setOpening(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={dismiss} aria-hidden />
      <div
        className="relative w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 shadow-xl p-6 space-y-4"
        role="dialog"
        aria-labelledby="store-closed-reminder-title"
        aria-modal="true"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-2xl">
            🚪
          </div>
          <div>
            <h2
              id="store-closed-reminder-title"
              className="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              Heads up — your store is closed
            </h2>
            <p className="mt-1 text-slate-600 dark:text-slate-300 text-sm">
              You have your store turned off. Customers can&apos;t place orders until you open it
              back up. Whenever you&apos;re ready, tap below or visit Settings.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="button"
            onClick={handleOpenStore}
            disabled={opening}
            className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-70 text-white font-semibold transition-colors min-h-[48px]"
          >
            {opening ? 'Opening…' : 'Open my store'}
          </button>
          <Link
            to={`${prefix}/owner/settings`}
            onClick={dismiss}
            className="flex-1 py-3 rounded-xl border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-medium text-center min-h-[48px] flex items-center justify-center"
          >
            Go to Settings
          </Link>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="block w-full text-center text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          Keep store closed for now
        </button>
      </div>
    </div>
  );
}
